import { buildQuickEquipArmor } from "@/data/vipQuickEquipPresets";
import {
    createKitPlacementFromTemplate,
    getAutoPlaceFailureReason,
    getCatalogEntry,
    normalizeItemCategory,
    resolveAutoPlaceTarget,
    snapQuantityForEntry,
} from "@/data/vipSimulatorCatalog";
import {
    canPlaceClothingInSlot,
    getClothingPlacementBlockReason,
    isArmorClothingEntry,
    isOutfitEntry,
} from "@/data/vipClothingRules";
import { attachmentFitsWeapon } from "@/data/weaponAttachments";
import { getWeaponKeyFromCatalogEntry } from "@/data/weaponSpecs";
import { getLang, translate } from "@/i18n/I18nProvider";
import { itemName } from "@/i18n/items";
import { formatQty } from "./vipSimulatorUtils";

/** Nome do item no idioma atual (catálogo usa nomes PT). */
function displayItemName(entry) {
    return itemName(entry?.name, getLang());
}

/** @typedef {{ kind: string, category?: string, index?: number, accept?: string, weaponZone?: string, weaponIndex?: number, attachSlot?: string }} KitLocation */

export function cloneSim(sim) {
    const clonePlacement = (p) => {
        if (!p) return null;
        const c = { ...p };
        if (p.attachments) {
            c.attachments = Object.fromEntries(
                Object.entries(p.attachments).map(([k, v]) => [k, v ? { ...v } : null]),
            );
        }
        return c;
    };
    return {
        /* Pool é catálogo fixo — manter referência evita recarregar centenas de ícones a cada mudança no kit. */
        pool: sim.pool,
        inv: sim.inv.map(clonePlacement),
        hotbar: sim.hotbar.map(clonePlacement),
        armor: Object.fromEntries(Object.entries(sim.armor).map(([k, v]) => [k, clonePlacement(v)])),
    };
}

/** @returns {KitLocation | null} */
export function findLocation(sim, instanceId) {
    for (const category of Object.keys(sim.pool)) {
        const arr = sim.pool[category];
        for (let i = 0; i < arr.length; i++) {
            if (arr[i]?.instanceId === instanceId) return { kind: "pool", category, index: i };
        }
    }
    for (let i = 0; i < sim.inv.length; i++) {
        const p = sim.inv[i];
        if (p?.instanceId === instanceId) return { kind: "inv", index: i };
        const att = findInAttachments(p, instanceId);
        if (att) return { kind: "weapon-attach", ...att };
    }
    for (let i = 0; i < sim.hotbar.length; i++) {
        const p = sim.hotbar[i];
        if (p?.instanceId === instanceId) return { kind: "hotbar", index: i };
        const att = findInAttachments(p, instanceId);
        if (att) return { kind: "weapon-attach", ...att };
    }
    for (const accept of Object.keys(sim.armor)) {
        if (sim.armor[accept]?.instanceId === instanceId) return { kind: "armor", accept };
    }
    return null;
}

/** Primeira ocorrência no kit (inventário / cinto / equipamento), sem acessórios de arma. */
export function findKitPlacementByCatalogId(sim, catalogId) {
    for (let i = 0; i < sim.inv.length; i++) {
        const p = sim.inv[i];
        if (p?.catalogId === catalogId) return { kind: "inv", index: i };
    }
    for (let i = 0; i < sim.hotbar.length; i++) {
        const p = sim.hotbar[i];
        if (p?.catalogId === catalogId) return { kind: "hotbar", index: i };
    }
    for (const accept of Object.keys(sim.armor)) {
        if (sim.armor[accept]?.catalogId === catalogId) return { kind: "armor", accept };
    }
    return null;
}

function findInAttachments(weaponPlacement, instanceId) {
    if (!weaponPlacement?.attachments) return null;
    for (const [slot, p] of Object.entries(weaponPlacement.attachments)) {
        if (p?.instanceId === instanceId) {
            return { attachSlot: slot, weaponInstanceId: weaponPlacement.instanceId };
        }
    }
    return null;
}

/** @param {object} sim @param {KitLocation} loc */
export function getPlacementAt(sim, loc) {
    if (loc.kind === "pool") return sim.pool[loc.category][loc.index];
    if (loc.kind === "inv") return sim.inv[loc.index];
    if (loc.kind === "hotbar") return sim.hotbar[loc.index];
    if (loc.kind === "armor") return sim.armor[loc.accept];
    if (loc.kind === "weapon-attach") {
        const weapon = findWeaponPlacement(sim, loc.weaponInstanceId);
        return weapon?.attachments?.[loc.attachSlot] ?? null;
    }
    return null;
}

export function findWeaponPlacement(sim, instanceId) {
    for (const p of sim.inv) {
        if (p?.instanceId === instanceId) return p;
    }
    for (const p of sim.hotbar) {
        if (p?.instanceId === instanceId) return p;
    }
    return null;
}

function getWeaponParentLocation(sim, weaponInstanceId) {
    for (let i = 0; i < sim.inv.length; i++) {
        if (sim.inv[i]?.instanceId === weaponInstanceId) return { kind: "inv", index: i };
    }
    for (let i = 0; i < sim.hotbar.length; i++) {
        if (sim.hotbar[i]?.instanceId === weaponInstanceId) return { kind: "hotbar", index: i };
    }
    return null;
}

/** @param {object} sim @param {KitLocation} loc */
function clearAt(sim, loc) {
    if (loc.kind === "pool") return;
    if (loc.kind === "inv") sim.inv[loc.index] = null;
    else if (loc.kind === "hotbar") sim.hotbar[loc.index] = null;
    else if (loc.kind === "armor") sim.armor[loc.accept] = null;
    else if (loc.kind === "weapon-attach") {
        const weapon = findWeaponPlacement(sim, loc.weaponInstanceId);
        if (weapon?.attachments) weapon.attachments[loc.attachSlot] = null;
    }
}

/** @param {object} sim @param {KitLocation} loc @param {object} placement */
function setAt(sim, loc, placement) {
    if (loc.kind === "inv") sim.inv[loc.index] = placement;
    else if (loc.kind === "hotbar") sim.hotbar[loc.index] = placement;
    else if (loc.kind === "armor") sim.armor[loc.accept] = placement;
    else if (loc.kind === "weapon-attach") {
        const weapon = findWeaponPlacement(sim, loc.weaponInstanceId);
        if (weapon?.attachments) weapon.attachments[loc.attachSlot] = placement;
    }
}

function setSlotTarget(sim, target, placement) {
    if (target.zone === "inv") sim.inv[target.index] = placement;
    else if (target.zone === "hotbar") sim.hotbar[target.index] = placement;
    else if (target.zone === "armor") sim.armor[target.accept] = placement;
}

function normalizePlacementQty(placement, entry, zone) {
    if (!entry.quantityEnabled || zone === "armor" || isArmorClothingEntry(entry) || isOutfitEntry(entry)) {
        return { ...placement, quantity: 1 };
    }
    return { ...placement, quantity: snapQuantityForEntry(entry, placement.quantity) };
}

function slotAccepts(sim, zone, accept, entry) {
    if (!entry) return false;
    if (isArmorClothingEntry(entry) || isOutfitEntry(entry)) {
        return canPlaceClothingInSlot(sim, zone, accept, entry);
    }
    if (zone === "armor") return false;
    return zone === "inv" || zone === "hotbar";
}

/** @returns {string | null} */
export function getPlacementBlockReason(sim, target, entry) {
    if (!entry || target.type !== "slot") return null;
    return getClothingPlacementBlockReason(sim, target.zone, target.accept, entry);
}

function placeDisplacedInKit(sim, displaced) {
    if (!displaced) return true;
    const entry = getCatalogEntry(displaced.catalogId);
    if (!entry) return false;
    const target = resolveAutoPlaceTarget(entry, sim);
    if (!target || target.type !== "slot") return false;
    setSlotTarget(sim, target, displaced);
    return true;
}

/**
 * Soma a quantidade do modelo no pool à pilha já existente no kit.
 * @param {object} sim
 * @param {string} poolInstanceId
 * @param {KitLocation} existingLoc
 */
export function mergePoolQuantityIntoKit(sim, poolInstanceId, existingLoc) {
    const from = findLocation(sim, poolInstanceId);
    if (!from || from.kind !== "pool") return null;

    const template = getPlacementAt(sim, from);
    const entry = getCatalogEntry(template?.catalogId);
    if (!template || !entry?.quantityEnabled) return null;

    const existing = getPlacementAt(sim, existingLoc);
    if (!existing) return null;

    const next = cloneSim(sim);
    const cur = getPlacementAt(next, existingLoc);
    const newQty = snapQuantityForEntry(entry, cur.quantity + template.quantity);
    setAt(next, existingLoc, { ...cur, quantity: newQty });
    return next;
}

/**
 * Adiciona cópia do item do pool ao kit. O modelo em «Itens disponíveis» permanece.
 * @param {object} sim
 * @param {string} poolInstanceId
 * @param {object} target
 */
export function addFromPoolToKit(sim, poolInstanceId, target) {
    const from = findLocation(sim, poolInstanceId);
    if (!from || from.kind !== "pool") return null;

    const template = getPlacementAt(sim, from);
    const entry = getCatalogEntry(template?.catalogId);
    if (!template || !entry) return null;

    if (target.type === "slot" && !slotAccepts(sim, target.zone, target.accept, entry)) return null;

    const moving = createKitPlacementFromTemplate(template);
    if (!moving) return null;

    const next = cloneSim(sim);
    const norm = normalizePlacementQty(moving, entry, target.type === "slot" ? target.zone : null);

    if (target.type === "weapon-attach") {
        return applyWeaponAttach(next, norm, entry, target, null);
    }

    const displaced = getSlotPlacement(next, target);
    setSlotTarget(next, target, norm);

    if (displaced && !placeDisplacedInKit(next, displaced)) {
        return null;
    }

    return next;
}

/** @deprecated Use addFromPoolToKit */
export const moveFromPoolToKit = addFromPoolToKit;

/**
 * Resultado de tentar adicionar item do pool ao kit (sem efeitos secundários).
 * @param {object} sim
 * @param {string} poolInstanceId
 * @param {{ mergeConfirmed?: boolean }} [options]
 * @returns {{ sim: object, feedback: { type: 'success'|'error', message: string, id: string } | null }}
 */
export function planAddFromPool(sim, poolInstanceId, options = {}) {
    const { mergeConfirmed = false } = options;
    const loc = findLocation(sim, poolInstanceId);
    if (!loc || loc.kind !== "pool") return { sim, feedback: null };

    const template = getPlacementAt(sim, loc);
    if (!template) return { sim, feedback: null };

    const entry = getCatalogEntry(template.catalogId);
    if (!entry) return { sim, feedback: null };

    const catalogId = template.catalogId;
    const existing = findKitPlacementByCatalogId(sim, catalogId);

    if (existing) {
        if (!entry.quantityEnabled) {
            return {
                sim,
                feedback: {
                    type: "error",
                    message: translate("simulator.toast.alreadyInKit", { name: displayItemName(entry) }),
                    id: `kit-dup-${catalogId}`,
                },
            };
        }

        if (!mergeConfirmed) {
            return { sim, feedback: null, needsMergeConfirm: true };
        }

        const cur = getPlacementAt(sim, existing);
        const newQty = snapQuantityForEntry(entry, cur.quantity + template.quantity);
        const next = mergePoolQuantityIntoKit(sim, poolInstanceId, existing);
        if (!next) return { sim, feedback: null };

        return {
            sim: next,
            feedback: {
                type: "success",
                message: translate("simulator.toast.qtyUpdated", {
                    name: displayItemName(entry),
                    qty: formatQty(newQty),
                }),
                id: `kit-merge-${catalogId}`,
            },
        };
    }

    const target = resolveAutoPlaceTarget(entry, sim);
    if (!target) {
        return {
            sim,
            feedback: {
                type: "error",
                message: getAutoPlaceFailureReason(entry, sim),
                id: `kit-place-fail-${catalogId}`,
            },
        };
    }

    const blockReason = getPlacementBlockReason(sim, target, entry);
    const next = addFromPoolToKit(sim, poolInstanceId, target);
    if (next) {
        return {
            sim: next,
            feedback: {
                type: "success",
                message: translate("simulator.toast.added", { name: displayItemName(entry) }),
                id: `kit-add-${catalogId}`,
            },
        };
    }

    return {
        sim,
        feedback: {
            type: "error",
            message: blockReason || translate("simulator.toast.addFailed"),
            id: `kit-add-block-${catalogId}`,
        },
    };
}

function getSlotPlacement(sim, target) {
    if (target.type !== "slot") return null;
    if (target.zone === "inv") return sim.inv[target.index];
    if (target.zone === "hotbar") return sim.hotbar[target.index];
    if (target.zone === "armor") return sim.armor[target.accept];
    return null;
}

/**
 * Reorganize within kit only — swap slots, never return to pool.
 * @param {object} sim
 * @param {string} instanceId
 * @param {object} target
 */
export function moveWithinKit(sim, instanceId, target) {
    const from = findLocation(sim, instanceId);
    if (!from || from.kind === "pool") return null;

    const raw = getPlacementAt(sim, from);
    const entry = getCatalogEntry(raw?.catalogId);
    if (!raw || !entry) return null;

    if (target.type === "pool") return null;

    if (target.type === "slot" && !slotAccepts(sim, target.zone, target.accept, entry)) return null;

    const next = cloneSim(sim);
    const moving = { ...getPlacementAt(next, from) };
    clearAt(next, from);

    const norm = normalizePlacementQty(moving, entry, target.type === "slot" ? target.zone : "inv");

    if (target.type === "weapon-attach") {
        const result = applyWeaponAttach(next, norm, entry, target, from);
        if (!result) {
            setAt(next, from, moving);
        }
        return result;
    }

    const displaced = getSlotPlacement(next, target);
    setSlotTarget(next, target, norm);

    if (displaced) {
        if (from.kind === "weapon-attach") {
            applyWeaponAttach(next, displaced, getCatalogEntry(displaced.catalogId), {
                type: "weapon-attach",
                weaponInstanceId: from.weaponInstanceId,
                attachSlot: from.attachSlot,
            });
        } else {
            setAt(next, from, displaced);
        }
    }

    return next;
}

function applyWeaponAttach(sim, moving, entry, target, from = null) {
    if (entry.cat !== "attachment") return null;

    const weapon = findWeaponPlacement(sim, target.weaponInstanceId);
    if (!weapon) return null;

    const weaponEntry = getCatalogEntry(weapon.catalogId);
    const weaponKey = getWeaponKeyFromCatalogEntry(weaponEntry);
    if (!attachmentFitsWeapon(entry, weaponKey)) return null;
    if (entry.attachmentSlot !== target.attachSlot) return null;

    const parent = getWeaponParentLocation(sim, target.weaponInstanceId);
    if (!parent) return null;

    if (!weapon.attachments) weapon.attachments = {};
    if (!(target.attachSlot in weapon.attachments)) return null;

    const displaced = weapon.attachments[target.attachSlot];
    weapon.attachments[target.attachSlot] = { ...moving, quantity: 1 };

    if (displaced) {
        if (from?.kind === "weapon-attach") {
            const w = findWeaponPlacement(sim, from.weaponInstanceId);
            if (w?.attachments) w.attachments[from.attachSlot] = displaced;
        } else if (from?.kind) {
            setAt(sim, from, displaced);
        }
    }

    return sim;
}

/** Equipa acessório (cópia do pool) numa arma — o modelo no pool permanece. */
export function equipAttachmentFromPool(sim, weaponInstanceId, attachmentInstanceId) {
    const from = findLocation(sim, attachmentInstanceId);
    if (!from || from.kind !== "pool") return null;

    const template = getPlacementAt(sim, from);
    const entry = getCatalogEntry(template?.catalogId);
    if (!template || !entry || entry.cat !== "attachment") return null;

    const moving = createKitPlacementFromTemplate(template);
    if (!moving) return null;
    moving.quantity = 1;

    const weapon = findWeaponPlacement(sim, weaponInstanceId);
    const weaponEntry = getCatalogEntry(weapon?.catalogId);
    const weaponKey = getWeaponKeyFromCatalogEntry(weaponEntry);
    if (!attachmentFitsWeapon(entry, weaponKey)) return null;

    const attachSlot = entry.attachmentSlot;
    if (!attachSlot) return null;

    const next = cloneSim(sim);
    return applyWeaponAttach(
        next,
        moving,
        entry,
        { type: "weapon-attach", weaponInstanceId, attachSlot },
        null,
    );
}

/** Remove acessório da arma (continua disponível na lista de itens). */
export function unequipAttachmentFromWeapon(sim, weaponInstanceId, attachSlot) {
    const weapon = findWeaponPlacement(sim, weaponInstanceId);
    if (!weapon?.attachments?.[attachSlot]) return null;

    const next = cloneSim(sim);
    const w = findWeaponPlacement(next, weaponInstanceId);
    if (!w?.attachments) return null;

    w.attachments[attachSlot] = null;
    return next;
}

/** @deprecated */
export const unequipAttachmentToPool = unequipAttachmentFromWeapon;

/** @param {object} over @param {object} sim */
export function parseDropTarget(over, sim) {
    if (!over) return null;
    const t = over.data?.current;
    if (t?.type === "slot") return t;
    if (t?.type === "weapon-attach") return t;
    const id = String(over.id || "");
    if (id.startsWith("slot-inv-")) return { type: "slot", zone: "inv", index: parseInt(id.replace("slot-inv-", ""), 10) };
    if (id.startsWith("slot-hotbar-")) return { type: "slot", zone: "hotbar", index: parseInt(id.replace("slot-hotbar-", ""), 10) };
    if (id.startsWith("slot-armor-")) return { type: "slot", zone: "armor", accept: id.replace("slot-armor-", "") };
    if (id.startsWith("slot-wattach-")) {
        const parts = id.replace("slot-wattach-", "").split("--");
        return {
            type: "weapon-attach",
            weaponInstanceId: parts[0],
            attachSlot: parts[1],
        };
    }
    if (t?.type === "placement") {
        const loc = findLocation(sim, t.placement.instanceId);
        if (!loc) return null;
        if (loc.kind === "inv") return { type: "slot", zone: "inv", index: loc.index };
        if (loc.kind === "hotbar") return { type: "slot", zone: "hotbar", index: loc.index };
        if (loc.kind === "armor") return { type: "slot", zone: "armor", accept: loc.accept };
        if (loc.kind === "weapon-attach") {
            return {
                type: "weapon-attach",
                weaponInstanceId: loc.weaponInstanceId,
                attachSlot: loc.attachSlot,
            };
        }
    }
    return null;
}

/** Collect placements for pricing (weapons + attachments). */
export function forEachKitPlacement(sim, fn) {
    const walk = (p, zone, extra) => {
        if (!p) return;
        fn(p, zone, extra);
        if (p.attachments) {
            Object.values(p.attachments).forEach((att) => {
                if (att) fn(att, zone, { ...extra, attachment: true });
            });
        }
    };
    sim.hotbar.forEach((p, i) => walk(p, "hotbar", { index: i }));
    sim.inv.forEach((p, i) => walk(p, "inv", { index: i }));
    Object.entries(sim.armor).forEach(([accept, p]) => walk(p, "armor", { accept }));
}

export function collectPlacementsForPool(sim, instanceId) {
    const loc = findLocation(sim, instanceId);
    if (!loc) return [];
    const p = getPlacementAt(sim, loc);
    if (!p) return [];

    if (loc.kind === "weapon-attach") return [p];

    const out = [];
    if (p.attachments) {
        Object.values(p.attachments).forEach((att) => {
            if (att) out.push(att);
        });
    }
    out.push(p);
    return out;
}

/** Remove item do kit (a lista «Itens disponíveis» não muda). */
export function removeFromKit(sim, instanceId) {
    const loc = findLocation(sim, instanceId);
    if (!loc || loc.kind === "pool") return null;

    const next = cloneSim(sim);
    clearAt(next, loc);
    return next;
}

/** @deprecated */
export const removeFromKitToPool = removeFromKit;

/**
 * Aplica preset de equipamento rápido (substitui os 7 slots de Equipável).
 * @param {object} sim
 * @param {string} presetId
 * @returns {{ sim: object, missing: string[] } | null}
 */
export function applyQuickEquipPreset(sim, presetId) {
    const built = buildQuickEquipArmor(sim, presetId);
    if (!built) return null;

    const next = cloneSim(sim);
    next.armor = built.armor;
    return { sim: next, missing: built.missing };
}
